import { useTheme } from "@mui/material/styles";
import { APIProvider, type ColorScheme } from "@vis.gl/react-google-maps";

/**
 * ccTLD the Maps JS API biases geocoding, Places and address formatting to.
 * Every agency we serve is Canadian; this is the single line to change (or to
 * derive from the API region — see `src/api/region.ts` in either app) when the
 * US region goes live.
 */
export const GOOGLE_MAPS_REGION = "CA";

/**
 * Cloud-styled map id shared by every Rubicon map. Also what switches Google to
 * the vector renderer, which `AdvancedMarker` requires.
 */
export const GOOGLE_MAP_ID = "4380cd85df35e961";

/**
 * Google colour scheme matching the MUI theme's mode. Pass it as `colorScheme`
 * on every `<Map>` so maps go dark with the app. Google only reads it when a
 * map is created; `@vis.gl/react-google-maps` recreates the map when it
 * changes, so toggling the mode re-renders the map in the new scheme.
 * String values rather than the `ColorScheme` object so test mocks of
 * `@vis.gl/react-google-maps` don't need to provide it.
 */
export const useMapColorScheme = (): ColorScheme =>
  useTheme().palette.mode === "dark" ? "DARK" : "LIGHT";

/** Union of the libraries every consumer needs — see the note on the provider. */
const GOOGLE_MAPS_LIBRARIES = ["places", "geocoding"];

export interface GoogleMapsProviderProps {
  /** Defaults to `VITE_GOOGLE_PLACES_KEY`, which both apps and Storybook define. */
  apiKey?: string;
  /** ISO 639-1 code for map labels and controls. Google's own default when omitted. */
  language?: string;
  children: React.ReactNode;
}

/**
 * Bootstraps the Google Maps JS API. Wrap anything that renders a map or calls a
 * `@vis.gl/react-google-maps` hook — once at the app root (services) or at each
 * map entry point (portal).
 *
 * This is the only place allowed to load the API. It is a page-level singleton:
 * a second load with different parameters re-runs its `customElements.define`
 * calls, which fail, leaving the map unable to authenticate and stuck at
 * `renderingType: UNINITIALIZED` — no tiles, no events, no markers. So the
 * parameters live here, identical for every mount, rather than at the call
 * sites. `googleMapsSingleLoader.test.ts` in the portal guards this.
 */
export const GoogleMapsProvider: React.FC<GoogleMapsProviderProps> = ({
  apiKey,
  language,
  children,
}) => (
  <APIProvider
    apiKey={apiKey ?? import.meta.env.VITE_GOOGLE_PLACES_KEY}
    libraries={GOOGLE_MAPS_LIBRARIES}
    language={language}
    region={GOOGLE_MAPS_REGION}
  >
    {children}
  </APIProvider>
);
